import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { Button, FormControl } from '../ui';
import RequestObservationsModal from '../Requests/RequestObservationsModal';
import {
  ORIGINATION_BOARD_COLUMNS,
  ORIGINATION_COMPLETABLE_STATUSES,
  ORIGINATION_CONTINUABLE_STATUSES,
  ORIGINATION_DISCARDABLE_STATUSES
} from '../../config/originationBoardColumns';
import { discardApprovedRequest, fetchRequests } from '../../services/comersRequestFlow';
import {
  getObservationOwnerContext,
  sortRequestsByObservationOwner
} from '../../utils/observationOwner';
import OriginationsBoardColumn from './OriginationsBoardColumn';

const MySwal = withReactContent(Swal);

const isSearchMatch = (request, search) => {
  const query = search.trim().toLowerCase();
  if (!query) return true;

  const haystack = [
    request.customer,
    request.dni,
    request.id,
    request.uid,
    request.status_label,
    request.relevant_observation?.description,
    request.relevant_observation?.action_owner_label
  ].filter(Boolean).join(' ').toLowerCase();

  return haystack.includes(query);
};

const OriginationsBoard = () => {
  const navigate = useNavigate();
  const [requestsByColumn, setRequestsByColumn] = useState({});
  const [loadingByColumn, setLoadingByColumn] = useState({});
  const [loadedColumns, setLoadedColumns] = useState({});
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedRequestForObservations, setSelectedRequestForObservations] = useState(null);

  const loadColumn = useCallback(async (column) => {
    setLoadingByColumn((prev) => ({ ...prev, [column.key]: true }));
    setErrorMessage('');

    try {
      const requests = await fetchRequests({
        statuses: column.statuses,
        limit: column.key === 'closed' ? 50 : 100
      });

      setRequestsByColumn((prev) => ({ ...prev, [column.key]: requests }));
      setLoadedColumns((prev) => ({ ...prev, [column.key]: true }));
    } catch (error) {
      console.error(`Error loading originations board column ${column.key}:`, error);
      setRequestsByColumn((prev) => ({ ...prev, [column.key]: [] }));
      setErrorMessage('No se pudieron cargar algunas solicitudes del tablero.');
    } finally {
      setLoadingByColumn((prev) => ({ ...prev, [column.key]: false }));
    }
  }, []);

  const loadOperationalColumns = useCallback(async () => {
    const columnsToLoad = ORIGINATION_BOARD_COLUMNS.filter((column) => !column.isDeferred);
    await Promise.all(columnsToLoad.map((column) => loadColumn(column)));
  }, [loadColumn]);

  useEffect(() => {
    loadOperationalColumns();
  }, [loadOperationalColumns]);

  const handleRefresh = async () => {
    setRefreshing(true);

    try {
      const loadedDeferredColumns = ORIGINATION_BOARD_COLUMNS.filter(
        (column) => column.isDeferred && loadedColumns[column.key]
      );
      await Promise.all([
        loadOperationalColumns(),
        ...loadedDeferredColumns.map((column) => loadColumn(column))
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const handleViewProfile = (request) => {
    if (!request.dni) return;
    navigate(`/clientes/${request.dni}`);
  };

  const handleContinue = (request) => {
    if (!ORIGINATION_CONTINUABLE_STATUSES.has(Number(request.status)) || !request.dni) return;

    navigate(`/clientes/${request.dni}`, {
      state: {
        pendingAction: 'approved-request',
        approvedRequestUid: request.uid,
        resumeFlowVariant: request.resume_flow_variant || 'approved'
      }
    });
  };

  const handleComplete = (request) => {
    if (!ORIGINATION_COMPLETABLE_STATUSES.has(Number(request.status)) || !request.dni) return;

    navigate(`/clientes/${request.dni}`, {
      state: {
        pendingAction: 'approved-request',
        approvedRequestUid: request.uid,
        resumeFlowVariant: request.resume_flow_variant || 'initial'
      }
    });
  };

  const handleOpenObservations = (request) => {
    setSelectedRequestForObservations(request);
  };

  const handleDiscard = async (request) => {
    if (!ORIGINATION_DISCARDABLE_STATUSES.has(Number(request.status))) return;

    const result = await MySwal.fire({
      icon: 'warning',
      title: '¿Descartar solicitud?',
      html: `Se marcará como <strong>oferta desistida</strong> para <strong>${request.customer || 'el cliente'}</strong> (DNI ${request.dni || 'N/A'}). Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, descartar',
      cancelButtonText: 'Cancelar',
      focusCancel: true
    });

    if (!result.isConfirmed) return;

    try {
      await discardApprovedRequest(request.uid);
      setRequestsByColumn((prev) => {
        const next = {};
        Object.entries(prev).forEach(([columnKey, requests]) => {
          next[columnKey] = requests.filter((item) => item.uid !== request.uid);
        });
        return next;
      });
      await MySwal.fire({
        icon: 'success',
        title: 'Solicitud descartada',
        timer: 2200,
        showConfirmButton: false
      });
    } catch (error) {
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'No se pudo descartar la solicitud.'
      });
    }
  };

  const searchFilteredRequestsByColumn = useMemo(
    () => ORIGINATION_BOARD_COLUMNS.reduce((acc, column) => {
      acc[column.key] = (requestsByColumn[column.key] || []).filter((request) => isSearchMatch(request, search));
      return acc;
    }, {}),
    [requestsByColumn, search]
  );

  const filteredRequestsByColumn = useMemo(
    () => ORIGINATION_BOARD_COLUMNS.reduce((acc, column) => {
      let requests = searchFilteredRequestsByColumn[column.key] || [];

      if (column.key === 'observed') {
        requests = sortRequestsByObservationOwner(requests);
      }

      acc[column.key] = requests;
      return acc;
    }, {}),
    [searchFilteredRequestsByColumn]
  );

  const getColumnCommercialAction = (column, requests) => {
    if (column.key === 'observed') {
      return requests.some((request) => getObservationOwnerContext(request.relevant_observation).isCommercialTurn);
    }
    return Boolean(column.requiresCommercialAction);
  };

  const canContinueRequest = (request) => ORIGINATION_CONTINUABLE_STATUSES.has(Number(request.status));
  const canCompleteRequest = (request) => ORIGINATION_COMPLETABLE_STATUSES.has(Number(request.status));
  const canDiscardRequest = (request) => ORIGINATION_DISCARDABLE_STATUSES.has(Number(request.status));
  const canOpenObservations = (request) => Boolean(
    request.relevant_observation || Number(request.open_observations_count || 0) > 0
  );

  return (
    <div className="originations-board">
      <div className="originations-board__topbar">
        <FormControl
          id="originations-board-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar por N° solicitud, cliente, DNI o estado"
          iconLeft={<i className="bi bi-search" aria-hidden />}
          className="originations-board__search"
        />
        <Button
          variant="outlineSecondary"
          onClick={handleRefresh}
          disabled={refreshing}
          className="originations-board__refresh"
        >
          <i className={`bi bi-arrow-clockwise${refreshing ? ' rg-spin' : ''}`} aria-hidden />
          Actualizar
        </Button>
      </div>

      {errorMessage && (
        <div className="alert alert-warning originations-board__alert" role="alert">
          {errorMessage}
        </div>
      )}

      <section className="originations-board__kanban" aria-label="Tablero kanban de originación">
        {ORIGINATION_BOARD_COLUMNS.map((column) => {
          const columnRequests = filteredRequestsByColumn[column.key] || [];

          return (
          <OriginationsBoardColumn
            key={column.key}
            column={column}
            count={columnRequests.length}
            isCommercialAction={getColumnCommercialAction(column, columnRequests)}
            isLoading={Boolean(loadingByColumn[column.key])}
            isLoaded={Boolean(loadedColumns[column.key])}
            requests={columnRequests}
            canContinueRequest={canContinueRequest}
            canCompleteRequest={canCompleteRequest}
            canDiscardRequest={canDiscardRequest}
            canOpenObservations={canOpenObservations}
            onLoadDeferred={loadColumn}
            onViewProfile={handleViewProfile}
            onContinue={handleContinue}
            onComplete={handleComplete}
            onOpenObservations={handleOpenObservations}
            onDiscard={handleDiscard}
          />
          );
        })}
      </section>

      <RequestObservationsModal
        open={Boolean(selectedRequestForObservations)}
        request={selectedRequestForObservations}
        onClose={() => setSelectedRequestForObservations(null)}
      />
    </div>
  );
};

export default OriginationsBoard;
